import React from 'react';
import { Box, Button, Card, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { DeleteRounded, EditRounded } from '@mui/icons-material';

/**
 * The shared shape of every MasterChart list page (medications, allergies,
 * conditions, surgeries): one line on why as the page subtitle, one card per
 * entry, then the actions (Add, Back) under the list. The subtitle line stands in
 * for an empty-state box, so there isn't one.
 */

/** Setup only: the subtitle there is the MasterChart intro, so the why line moves here. */
export const RecordIntro = ({ text }) => <Typography sx={{ mb: 3 }}>{text}</Typography>;

/** Add and anything beside it. `spaced` once there are cards above it. */
export const RecordActions = ({ addLabel, onAdd, disabled = false, spaced = false, children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: spaced ? 3 : 0 }}>
    <Button variant="contained" onClick={onAdd} disabled={disabled}>
      {addLabel}
    </Button>
    {children}
  </Box>
);

/**
 * Name over one line of detail, edit and delete on the right. MedicationCard
 * draws the same row, plus an expand arrow for its reminders.
 */
export const RecordCard = ({ title, detail, onEdit, onDelete, editLabel, deleteLabel }) => (
  <Card>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, pr: 1, py: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {title}
        </Typography>
        {detail ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {detail}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <IconButton onClick={onEdit} aria-label={editLabel}>
          <EditRounded />
        </IconButton>
        <IconButton onClick={onDelete} aria-label={deleteLabel}>
          <DeleteRounded />
        </IconButton>
      </Box>
    </Box>
  </Card>
);

/** The card stack, or a bare spinner while loading. Empty renders nothing. */
export const RecordCardStack = ({ loading = false, children }) => {
  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  return <Stack spacing={1.5}>{children}</Stack>;
};

/** "a, b, c" from whichever parts are filled in. */
export const joinDetail = (...parts) => parts.filter(Boolean).join(', ');
